// ADR 6.10.26b §1, §4 — the browser that hands the install on.
//
// A browser that cannot install the PWA itself (`platformOf` answered
// `other`) can still hand the learner to one on the same device that can. A
// page cannot see which browsers are installed, so the target comes from what
// the **operating system guarantees**: Edge ships with Windows 10/11, Chrome
// is Android's usual default, and macOS and Linux get a link to copy because
// no URL opens Safari or Chrome from another browser.
//
// Pure, and reads no `window` and no i18n: the caller passes the UA, the
// origin and the search, so the whole contract is a `node` unit test. The
// origin is the caller's `window.location.origin`, never a constant: a handoff
// reopens the deployment the learner is on (production, a Pages preview,
// `localhost`), and the manifest's `"id": "/"` is per origin.
//
// Must not import the wasm facade: `main.tsx` imports this at boot.

import { EXCLUDED_DERIVATIVES, NOT_SAFARI } from './installAffordance'
import { entryFlag, INSTALL_ENTRY_PATH, INSTALL_PARAM } from './installDeepLink'

export type HandoffTarget = 'windows_edge' | 'android_chrome' | 'mac_copy' | 'linux_copy'

/** §4 — the fallback a missing Chrome sends the learner back with: `handoff=missing`. */
export const HANDOFF_PARAM = 'handoff'
const MISSING = 'missing'

/** `Chrome/`, not Edge, and none of 25.9.26h's derivatives. */
function isPlainChrome(userAgent: string): boolean {
  return userAgent.includes('Chrome/') && !userAgent.includes('Edg/') && !EXCLUDED_DERIVATIVES.test(userAgent)
}

function isSafari(userAgent: string): boolean {
  return userAgent.includes('Safari/') && !NOT_SAFARI.test(userAgent)
}

/** §1 rule 3 — Windows 10 and 11 both report `Windows NT 10.0`; older Windows has no guaranteed Edge. */
function windowsTarget(userAgent: string): HandoffTarget | null {
  if (!userAgent.includes('Windows NT 10.0')) return null
  return userAgent.includes('Edg/') ? null : 'windows_edge'
}

/**
 * An in-app WebView, or HarmonyOS NEXT (15.10.26c §3), which can neither
 * install nor reach a Chrome: a variant carrying `Linux` would otherwise reach
 * `linux_copy`.
 */
function hasNoHandoff(userAgent: string): boolean {
  return userAgent.includes('; wv)') || userAgent.includes('OpenHarmony')
}

/**
 * Where a browser that cannot install hands the learner on to, or null.
 *
 * Meaningful only for a UA `platformOf` answered `other` for: an iPadOS
 * desktop UA reads as `Macintosh` here, so the hook never asks for any other
 * platform. First match wins; the browser handed *to* always answers `null`,
 * so a handoff never loops.
 */
export function handoffTargetOf(userAgent: string): HandoffTarget | null {
  if (hasNoHandoff(userAgent)) return null
  if (userAgent.includes('Android')) return isPlainChrome(userAgent) ? null : 'android_chrome'
  if (userAgent.includes('Windows NT')) return windowsTarget(userAgent)
  if (userAgent.includes('Macintosh')) {
    return isSafari(userAgent) || isPlainChrome(userAgent) ? null : 'mac_copy'
  }
  if (userAgent.includes('Linux')) return isPlainChrome(userAgent) ? null : 'linux_copy'
  return null
}

/** The URL the learner lands on in the other browser — 6.10.26a's canonical entry. */
export function installUrl(origin: string): string {
  return `${origin}${INSTALL_ENTRY_PATH}?${INSTALL_PARAM}=1`
}

/**
 * Chrome by package, with a fallback that reloads this browser at the entry
 * plus `handoff=missing`. The fallback is encoded whole, so its own `?` and
 * `&` cannot end the intent's fragment early.
 */
function chromeIntent(origin: string): string {
  const { protocol, host } = new URL(origin)
  const fallback = encodeURIComponent(`${installUrl(origin)}&${HANDOFF_PARAM}=${MISSING}`)
  const path = `${INSTALL_ENTRY_PATH}?${INSTALL_PARAM}=1`
  const scheme = protocol.replace(/:$/, '')
  return `intent://${host}${path}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${fallback};end`
}

/** The link the panel's primary button follows, or null for the copy-only targets. */
export function handoffHref(target: HandoffTarget, origin: string): string | null {
  if (target === 'windows_edge') return `microsoft-edge:${installUrl(origin)}`
  if (target === 'android_chrome') return chromeIntent(origin)
  return null
}

/** §3 — the strip's title by target, since every handoff is `platform: 'other'`. */
export function deviceOfTarget(target: HandoffTarget): 'computer' | 'handheld' {
  return target === 'android_chrome' ? 'handheld' : 'computer'
}

/** §4 — `handoff=missing`, at top level or inside `next`, through 6.10.26a's one reader. */
export function chromeMissingIn(search: string): boolean {
  return entryFlag(search, HANDOFF_PARAM, MISSING)
}

// Module state, `installPrompt.ts`'s shape: latched at boot, before 6.10.26a
// strips the query and before any surface mounts, so every surface reads the
// same answer.
let missing = false

/** §4 — once, at boot, beside `startInstallCapture`. A later call never clears it. */
export function latchChromeMissing(search: string): void {
  missing = missing || chromeMissingIn(search)
}

/** §4 — the latched answer. */
export function chromeMissing(): boolean {
  return missing
}

/** Test-only: clears the latch. */
export function resetHandoffForTests(): void {
  missing = false
}
